import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import Layout from "@/components/Layout";
import SEO from "@/components/SEO";
import MarkdownToolbar from "@/components/MarkdownToolbar";
import AlignmentToggle from "@/components/AlignmentToggle";
import { POLL_COLOR_PALETTE, paletteColor } from "@/lib/pollColors";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

export default function SubmitPage() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [type, setType] = useState("essay");
  const [title, setTitle] = useState("");
  const [topic, setTopic] = useState("General");
  const [summary, setSummary] = useState("");
  const [titleAlign, setTitleAlign] = useState("left");
  const [summaryAlign, setSummaryAlign] = useState("left");
  const [body, setBody] = useState("");
  const [pollOptions, setPollOptions] = useState(["", ""]);
  const [chartType, setChartType] = useState("bar");
  const [optionColors, setOptionColors] = useState([paletteColor(0), paletteColor(1)]);
  const [topics, setTopics] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [agreed, setAgreed] = useState(false);

  useEffect(() => {
    api.get("/topics").then((r) => setTopics(r.data.topics || []));
  }, []);

  useEffect(() => {
    if (!loading && user === false) navigate("/login?next=/submit");
  }, [loading, user, navigate]);

  const setOpt = (i, v) => {
    const next = [...pollOptions];
    next[i] = v;
    setPollOptions(next);
  };
  const addOpt = () => {
    setPollOptions((p) => (p.length < 6 ? [...p, ""] : p));
    setOptionColors((c) => (c.length < 6 ? [...c, paletteColor(c.length)] : c));
  };
  const removeOpt = (i) => {
    setPollOptions((p) => (p.length > 2 ? p.filter((_, idx) => idx !== i) : p));
    setOptionColors((c) => (c.length > 2 ? c.filter((_, idx) => idx !== i) : c));
  };
  const setColor = (i, color) => setOptionColors((c) => c.map((v, idx) => (idx === i ? color : v)));

  const [mediaUrl, setMediaUrl] = useState("");
  const [mediaType, setMediaType] = useState("");
  const [mediaName, setMediaName] = useState("");
  const [uploading, setUploading] = useState(false);
  const [mediaSource, setMediaSource] = useState("upload"); // upload | youtube | vimeo
  const [embedUrl, setEmbedUrl] = useState("");
  const fileInputRef = useRef(null);
  const bodyRef = useRef(null);
  const isAdmin = user && user !== false && user.role === "admin";
  const MAX_UPLOAD_BYTES = (isAdmin ? 250 : 100) * 1024 * 1024;
  const MAX_UPLOAD_LABEL = isAdmin ? "250 MB" : "100 MB";

  const parseYouTube = (url) => {
    const patterns = [
      /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/|youtube\.com\/shorts\/)([a-zA-Z0-9_-]{11})/,
      /^([a-zA-Z0-9_-]{11})$/,
    ];
    for (const p of patterns) {
      const m = url.match(p);
      if (m) return m[1];
    }
    return null;
  };
  const parseVimeo = (url) => {
    const m = url.match(/vimeo\.com\/(?:video\/)?(\d+)/) || url.match(/^(\d{6,})$/);
    return m ? m[1] : null;
  };

  const applyEmbed = () => {
    const url = embedUrl.trim();
    if (!url) return;
    if (mediaSource === "youtube") {
      const id = parseYouTube(url);
      if (!id) { toast.error("Could not read that YouTube URL."); return; }
      setMediaUrl(id);
      setMediaType("youtube");
      setMediaName(`YouTube: ${id}`);
      toast.success("YouTube video linked.");
    } else if (mediaSource === "vimeo") {
      const id = parseVimeo(url);
      if (!id) { toast.error("Could not read that Vimeo URL."); return; }
      setMediaUrl(id);
      setMediaType("vimeo");
      setMediaName(`Vimeo: ${id}`);
      toast.success("Vimeo video linked.");
    }
  };

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > MAX_UPLOAD_BYTES) {
      toast.error(`File is over ${MAX_UPLOAD_LABEL}. Please choose a smaller file or use a YouTube / Vimeo link.`);
      e.target.value = "";
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setMediaUrl(data.url);
      setMediaType(data.media_type);
      setMediaName(file.name);
      toast.success(`${data.media_type === "video" ? "Video" : "Audio"} uploaded.`);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const openFilePicker = () => {
    if (mediaSource !== "upload") {
      setMediaSource("upload");
      setMediaUrl("");
      setMediaType("");
      setMediaName("");
      setEmbedUrl("");
    }
    fileInputRef.current?.click();
  };

  const wordCount = body.trim() ? body.trim().split(/\s+/).length : 0;
  const overLimit = type === "essay" && wordCount > 1000;

  const submit = async (e) => {
    e.preventDefault();
    if (overLimit) return;
    setSubmitting(true);
    try {
      const payload = { type, title, topic, summary, title_align: titleAlign, summary_align: summaryAlign };
      if (type === "poll") {
        payload.poll_options = pollOptions.filter((o) => o.trim());
        payload.chart_type = chartType;
        payload.chart_colors = optionColors.slice(0, payload.poll_options.length);
      } else {
        payload.body = body;
        if (type === "interview" && mediaUrl) {
          payload.media_url = mediaUrl;
          payload.media_type = mediaType;
        }
      }
      await api.post("/posts", payload);
      toast.success("Submitted. An editor will review your piece shortly.");
      navigate("/my-submissions");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSubmitting(false);
    }
  };

  if (loading || user === null) return <Layout><div className="max-w-[960px] mx-auto px-4 py-24 mono text-sm text-neutral-500">Loading&hellip;</div></Layout>;

  return (
    <Layout>
      <SEO
        path="/submit"
        title="Submit a piece"
        description="Contribute an essay, forum post, poll or interview to What Do We Want? When Do We Want It? All submissions are moderated before publication."
      />
      <div className="max-w-[960px] mx-auto px-4 py-12">
        <div className="mono text-xs uppercase tracking-[0.3em] text-amber-700">Contribute</div>
        <h1 className="serif text-5xl mt-3 tracking-tight">Submit a piece</h1>
        <p className="text-neutral-700 mt-4 leading-relaxed">
          All submissions are reviewed before being posted. Please write clearly, cite your reasoning, and remember: be respectful.
        </p>

        <form onSubmit={submit} className="mt-10 space-y-8">
          {/* Type selector */}
          <div>
            <label className="mono text-[10px] uppercase tracking-[0.25em] text-neutral-500">Format</label>
            <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
              {[
                { v: "essay", label: "Essay", hint: "opinions and articles" },
                { v: "forum", label: "Forum", hint: "A post with replies" },
                { v: "poll", label: "Poll", hint: "Cast your vote" },
                { v: "interview", label: "Interview", hint: "Own voice contributions" },
              ].map((t) => (
                <button
                  key={t.v}
                  type="button"
                  data-testid={`type-select-${t.v}`}
                  onClick={() => setType(t.v)}
                  className={`text-left p-4 border transition-colors ${
                    type === t.v ? "border-black bg-black text-white" : "border-neutral-300 hover:border-black"
                  }`}
                >
                  <div className="serif text-2xl">{t.label}</div>
                  <div className={`mono text-[10px] uppercase tracking-[0.2em] mt-1 ${type === t.v ? "text-amber-300" : "text-neutral-500"}`}>{t.hint}</div>
                </button>
              ))}
            </div>
          </div>

          {/* Title */}
          <div>
            <label className="mono text-[10px] uppercase tracking-[0.25em] text-neutral-500">Title (line breaks allowed)</label>
            <textarea
              data-testid="submit-title-input"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              required
              rows={2}
              className="mt-2 w-full serif text-3xl border-b border-neutral-300 focus:border-black bg-transparent py-2 focus:outline-none resize-y leading-tight"
              placeholder="Your headline goes here"
            />
            <AlignmentToggle value={titleAlign} onChange={setTitleAlign} testidPrefix="submit-title-align" />
          </div>

          {/* Summary */}
          <div>
            <label className="mono text-[10px] uppercase tracking-[0.25em] text-neutral-500">
              Standfirst (optional, max 300 chars)
            </label>
            <textarea
              data-testid="submit-summary-input"
              value={summary}
              onChange={(e) => setSummary(e.target.value.slice(0, 300))}
              rows={2}
              className="mt-2 w-full border border-neutral-300 focus:border-black bg-transparent p-3 focus:outline-none resize-none"
              placeholder="A brief introductory statement goes here"
            />
            <AlignmentToggle value={summaryAlign} onChange={setSummaryAlign} testidPrefix="submit-summary-align" />
            {type === "interview" && (
              <div className="mt-3 border border-neutral-300 p-3">
                <div className="mono text-[10px] uppercase tracking-[0.25em] text-neutral-500 mb-2">
                  Media source
                </div>
                <div className="flex gap-2 mb-3 flex-wrap">
                  {[
                    { v: "upload", label: "Upload file" },
                    { v: "youtube", label: "YouTube URL" },
                    { v: "vimeo", label: "Vimeo URL" },
                  ].map((s) => (
                    <button
                      key={s.v}
                      type="button"
                      data-testid={`media-source-${s.v}`}
                      onClick={() => {
                        if (s.v === "upload") {
                          openFilePicker();
                          return;
                        }
                        setMediaSource(s.v);
                        setMediaUrl("");
                        setMediaType("");
                        setMediaName("");
                        setEmbedUrl("");
                      }}
                      className={`mono text-xs uppercase tracking-[0.2em] px-3 py-1 border ${mediaSource === s.v ? "bg-black text-white border-black" : "border-neutral-300 hover:border-black"}`}
                    >
                      {s.v === "upload" && uploading ? "Uploading…" : s.label}
                    </button>
                  ))}
                </div>

                <input
                  ref={fileInputRef}
                  type="file"
                  accept="audio/*,video/*"
                  onChange={handleUpload}
                  className="hidden"
                  data-testid="interview-media-input"
                />

                {mediaSource === "upload" && (
                  <>
                    <div className="mono text-[10px] uppercase tracking-[0.25em] text-neutral-500 mb-2">
                      Upload audio or video (max {MAX_UPLOAD_LABEL}). For longer video, host on YouTube or Vimeo and paste the link.
                    </div>
                    {mediaUrl && (mediaType === "video" || mediaType === "audio") && (
                      <div className="mono text-xs text-neutral-700 flex items-center gap-2 flex-wrap">
                        <span className="uppercase tracking-[0.2em] text-amber-700">{mediaType}</span>
                        <span className="truncate max-w-[220px]" title={mediaName}>{mediaName}</span>
                        <button
                          type="button"
                          data-testid="interview-media-replace"
                          onClick={openFilePicker}
                          className="uppercase tracking-[0.2em] text-neutral-500 hover:text-black"
                        >
                          Replace
                        </button>
                        <button
                          type="button"
                          data-testid="interview-media-remove"
                          onClick={() => { setMediaUrl(""); setMediaType(""); setMediaName(""); }}
                          className="uppercase tracking-[0.2em] text-neutral-500 hover:text-red-700"
                        >
                          Remove
                        </button>
                      </div>
                    )}
                    {mediaUrl && mediaType === "video" && <video src={mediaUrl} controls className="mt-3 max-w-full max-h-80" />}
                    {mediaUrl && mediaType === "audio" && <audio src={mediaUrl} controls className="mt-3 w-full" />}
                  </>
                )}

                {(mediaSource === "youtube" || mediaSource === "vimeo") && (
                  <>
                    <div className="flex gap-2 items-center flex-wrap">
                      <input
                        type="url"
                        value={embedUrl}
                        onChange={(e) => setEmbedUrl(e.target.value)}
                        placeholder={mediaSource === "youtube" ? "https://youtube.com/watch?v=…" : "https://vimeo.com/…"}
                        data-testid={`embed-url-${mediaSource}`}
                        className="flex-1 min-w-[240px] border border-neutral-300 focus:border-black bg-transparent px-3 py-2 focus:outline-none"
                      />
                      <button type="button" onClick={applyEmbed} data-testid="embed-apply" className="bg-black text-white px-4 py-2 mono text-xs uppercase tracking-[0.2em] hover:bg-amber-700 transition-colors">
                        Link
                      </button>
                      {mediaUrl && (mediaType === "youtube" || mediaType === "vimeo") && (
                        <button type="button" onClick={() => { setMediaUrl(""); setMediaType(""); setMediaName(""); setEmbedUrl(""); }} className="mono text-xs uppercase tracking-[0.2em] text-neutral-500 hover:text-red-700">
                          Clear
                        </button>
                      )}
                    </div>
                    {mediaUrl && mediaType === "youtube" && (
                      <div className="mt-3 aspect-video max-w-full">
                        <iframe src={`https://www.youtube-nocookie.com/embed/${mediaUrl}?rel=0`} title="YouTube preview" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen className="w-full h-full border-0" />
                      </div>
                    )}
                    {mediaUrl && mediaType === "vimeo" && (
                      <div className="mt-3 aspect-video max-w-full">
                        <iframe src={`https://player.vimeo.com/video/${mediaUrl}`} title="Vimeo preview" allow="autoplay; fullscreen; picture-in-picture" allowFullScreen className="w-full h-full border-0" />
                      </div>
                    )}
                  </>
                )}
              </div>
            )}
          </div>

          {/* Body or Poll options */}
          {type === "poll" ? (
            <div className="space-y-6">
              <div>
                <label className="mono text-[10px] uppercase tracking-[0.25em] text-neutral-500">Chart type</label>
                <div className="mt-2 flex flex-wrap gap-2">
                  {["bar", "pie", "line"].map((ct) => (
                    <button
                      key={ct}
                      type="button"
                      data-testid={`chart-type-${ct}`}
                      onClick={() => setChartType(ct)}
                      className={`mono text-xs uppercase tracking-[0.2em] px-4 py-2 border ${chartType === ct ? "bg-black text-white border-black" : "border-neutral-300 hover:border-black"}`}
                    >
                      {ct}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className="mono text-[10px] uppercase tracking-[0.25em] text-neutral-500">Poll options (2 to 6) &mdash; pick a colour for each</label>
                <div className="mt-2 space-y-3">
                  {pollOptions.map((opt, i) => (
                    <div key={i} className="border border-neutral-200 p-3">
                      <div className="flex items-center gap-2">
                        <span className="mono text-xs text-neutral-500 w-6">{String.fromCharCode(65 + i)}.</span>
                        <input
                          data-testid={`poll-option-input-${i}`}
                          value={opt}
                          onChange={(e) => setOpt(i, e.target.value)}
                          className="flex-1 border border-neutral-300 focus:border-black bg-transparent px-3 py-2 focus:outline-none"
                          placeholder={`Option ${i + 1}`}
                        />
                        <span className="w-7 h-7 border border-neutral-300" style={{ background: optionColors[i] }} title="Selected colour" />
                        {pollOptions.length > 2 && (
                          <button type="button" data-testid={`poll-option-remove-${i}`} onClick={() => removeOpt(i)} className="mono text-xs uppercase tracking-[0.2em] text-neutral-500 hover:text-red-700">
                            Remove
                          </button>
                        )}
                      </div>
                      <div className="mt-2 flex flex-wrap gap-1.5 pl-8">
                        {POLL_COLOR_PALETTE.map((c) => (
                          <button
                            type="button"
                            key={c}
                            onClick={() => setColor(i, c)}
                            data-testid={`poll-color-${i}-${c}`}
                            aria-label={`Set colour ${c}`}
                            className={`w-6 h-6 border-2 ${optionColors[i] === c ? "border-black" : "border-transparent"}`}
                            style={{ background: c }}
                          />
                        ))}
                      </div>
                    </div>
                  ))}
                  {pollOptions.length < 6 && (
                    <button type="button" data-testid="poll-option-add" onClick={addOpt} className="mono text-xs uppercase tracking-[0.2em] border border-neutral-300 px-3 py-1 hover:border-black">
                      + Add option
                    </button>
                  )}
                </div>
              </div>
            </div>
          ) : (
            <div>
              <label className="mono text-[10px] uppercase tracking-[0.25em] text-neutral-500">
                {type === "essay" ? "Essay body (max 1000 words)" : "Forum prompt"}
              </label>
              <textarea
                ref={bodyRef}
                data-testid="submit-body-input"
                value={body}
                onChange={(e) => setBody(e.target.value)}
                rows={type === "essay" ? 16 : 8}
                required
                className="mt-2 w-full border border-neutral-300 focus:border-black bg-transparent p-4 focus:outline-none text-base leading-relaxed resize-y"
                placeholder={
                  type === "essay"
                    ? "Write or paste your essay text here"
                    : type === "interview"
                    ? "Please provide some information about who is being interviewed, when and what the discussion is about"
                    : "State your case here. Other users will be able to respond to your proposition or question."
                }
              />
              <MarkdownToolbar textareaRef={bodyRef} value={body} onChange={setBody} />
              {type === "essay" && (
                <div className={`mt-2 mono text-[10px] uppercase tracking-[0.25em] flex justify-end tabular-nums ${overLimit ? "text-red-800" : "text-neutral-500"}`}>
                  <span data-testid="essay-word-count">{wordCount} / 1000 words{overLimit ? " — over limit, please shorten" : ""}</span>
                </div>
              )}
            </div>
          )}

          <div className="border border-amber-700 bg-amber-50 p-4">
            <div className="mono text-[10px] uppercase tracking-[0.3em] text-amber-800">Editorial notice</div>
            <p className="text-sm text-neutral-800 mt-2 leading-relaxed">
              All submissions are reviewed by an editor before publication. <strong>The site editor
              reserves the right to shorten, edit, or amend submissions</strong> for clarity, length, tone, or
              legal reasons.
            </p>
            <label className="mt-3 flex items-start gap-2 text-sm text-neutral-800 cursor-pointer">
              <input
                type="checkbox"
                data-testid="submit-agree-checkbox"
                checked={agreed}
                onChange={(e) => setAgreed(e.target.checked)}
                className="mt-1 accent-black"
              />
              <span>I understand and agree to the editorial policy above.</span>
            </label>
          </div>

          <div className="flex justify-between items-center pt-4 border-t border-neutral-200">
            <Link to="/" className="mono text-xs uppercase tracking-[0.2em] text-neutral-600 hover:text-black">Cancel</Link>
            <button
              type="submit"
              data-testid="submit-post-button"
              disabled={submitting || !agreed || overLimit}
              className="bg-black text-white px-8 py-3 mono text-xs uppercase tracking-[0.2em] hover:bg-amber-700 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
            >
              {submitting ? "Submitting…" : "Submit for review"}
            </button>
          </div>
        </form>
      </div>
    </Layout>
  );
}
